// src/admin/components/Dashboard/AccountantDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  RiMoneyDollarCircleLine, RiBarChartBoxLine, RiFileChartLine, RiAlertLine,
  RiRefreshLine, RiArrowUpLine, RiArrowDownLine, RiReceiptLine,
  RiPieChartLine, RiArrowRightLine, RiCalendarLine, RiDownloadLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';

const AccountantDashboard = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);

  const fetchData = async (showRefresh = false) => {
    try {
      showRefresh ? setRefreshing(true) : setLoading(true);
      const year = new Date().getFullYear();
      const [statsRes, paymentsRes, revenueRes] = await Promise.all([
        api.get('/reports/dashboard-stats'),
        api.get('/reports/payment-logs', { params: { limit: 8 } }),
        api.get(`/reports/monthly-revenue/${year}`).catch(() => ({ data: { monthlyRevenue: [] } }))
      ]);
      setStats(statsRes.data?.stats || {});
      setRecentPayments(paymentsRes.data?.paymentLogs || []);
      const rev = revenueRes.data?.monthlyRevenue || [];
      // Build full 12-month array; fill missing months with 0 so chart always renders
      const currentMonth = new Date().getMonth() + 1; // 1-12
      const currentYear = new Date().getFullYear();
      const maxMonth = parseInt(year) === currentYear ? currentMonth : 12;
      const fullMonths = Array.from({ length: maxMonth }, (_, i) => {
        const m = i + 1;
        const found = rev.find(r => parseInt(r.month) === m);
        return found || { month: m, total_revenue: 0 };
      });
      setMonthlyRevenue(fullMonths);
    } catch (err) {
      console.error('Accountant dashboard error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const card = 'rounded-xl border shadow-md ' + (isDarkMode ? 'bg-gray-850 border-gray-700' : 'bg-white border-gray-200');
  const textMuted = isDarkMode ? 'text-zinc-400' : 'text-gray-500';
  const textMain = isDarkMode ? 'text-white' : 'text-gray-900';

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  const statCards = [
    {
      label: t('monthly_revenue'),
      value: fmt(stats?.monthly_revenue),
      icon: RiMoneyDollarCircleLine,
      iconBg: 'bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
      trend: 'up',
      path: '/admin/reports/revenue'
    },
    {
      label: t('pending_payments'),
      value: fmt(stats?.pending_payments_amount),
      sub: `${stats?.pending_payments_count || 0} ${t('pending')}`,
      icon: RiAlertLine,
      iconBg: 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400',
      trend: 'down',
      path: '/admin/payments?status=pending'
    },
    {
      label: t('total_revenue'),
      value: fmt((stats?.monthly_revenue || 0) * 12),
      sub: t('annual_revenue'),
      icon: RiBarChartBoxLine,
      iconBg: 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400',
      trend: 'up',
      path: '/admin/reports'
    },
    {
      label: t('active_tenants'),
      value: stats?.active_tenants ?? '—',
      sub: `${stats?.occupancy_rate ?? 0}% ${t('occupancy_rate')}`,
      icon: RiPieChartLine,
      iconBg: 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
      trend: 'up',
      path: '/admin/reports/occupancy'
    }
  ];

  const quickActions = [
    { label: t('record_payment'), icon: RiReceiptLine, path: '/admin/payments/add', color: 'text-purple-500' },
    { label: t('revenue_report'), icon: RiBarChartBoxLine, path: '/admin/reports/revenue', color: 'text-green-500' },
    { label: t('payment_logs'), icon: RiFileChartLine, path: '/admin/reports/payment-logs', color: 'text-blue-500' },
    { label: t('occupancy_report'), icon: RiPieChartLine, path: '/admin/reports/occupancy', color: 'text-orange-500' },
    { label: t('monthly_payments_report'), icon: RiCalendarLine, path: '/admin/reports/monthly-payments', color: 'text-red-500' },
  ];

  const maxRevenue = Math.max(1, ...monthlyRevenue.map(m => Number(m.total_revenue || 0)));

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className={`${card} p-6 animate-pulse`}>
            <div className="h-6 bg-gray-200 dark:bg-zinc-800 rounded w-1/3 mb-3" />
            <div className="h-8 bg-gray-200 dark:bg-zinc-800 rounded w-1/4" />
          </div>
        ))}
      </div>
    );
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${card} p-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${textMain}`}>
              {t('welcome_back')}, {user?.firstName}! 💼
            </h1>
            <p className={`mt-1 ${textMuted}`}>{t('overview_of_financial_performance')}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/admin/reports')}
              className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <RiDownloadLine size={16} /> {t('view_all_reports')}
            </button>
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-900 hover:bg-zinc-800 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'} transition-colors`}
            >
              <RiRefreshLine className={refreshing ? 'animate-spin' : ''} size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <div
            key={s.label}
            onClick={() => navigate(s.path)}
            className={`${card} p-5 cursor-pointer hover:shadow-lg transition-shadow`}
          >
            <div className="flex items-center justify-between mb-3">
              <p className={`text-sm font-medium ${textMuted}`}>{s.label}</p>
              <div className={`p-2 rounded-full ${s.iconBg}`}>
                <s.icon size={20} />
              </div>
            </div>
            <p className={`text-2xl font-bold ${textMain}`}>{s.value}</p>
            {s.sub && <p className={`text-xs mt-1 ${textMuted}`}>{s.sub}</p>}
            <div className={`flex items-center mt-2 text-xs ${s.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
              {s.trend === 'up' ? <RiArrowUpLine size={14} /> : <RiArrowDownLine size={14} />}
              <span className="ml-1">{t('vs_last_month')}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart (bar) */}
        <div className={`lg:col-span-2 ${card}`}>
          <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className={`font-semibold ${textMain} flex items-center gap-2`}>
              <RiBarChartBoxLine className="text-purple-500" /> {t('monthly_revenue_year', { year: new Date().getFullYear() })}
            </h3>
            <button onClick={() => navigate('/admin/reports/revenue')} className="text-sm text-red-500 hover:text-red-400 flex items-center gap-1">
              {t('view_details')} <RiArrowRightLine size={14} />
            </button>
          </div>
          <div className="p-5">
            {monthlyRevenue.length === 0 ? (
              <p className={`text-sm ${textMuted} text-center py-8`}>{t('no_revenue_data')}</p>
            ) : (
              <div className="flex items-end gap-2 h-40">
                {monthlyRevenue.map((m, i) => {
                  const height = maxRevenue > 0 ? Math.max((Number(m.total_revenue || 0) / maxRevenue) * 100, 4) : 4;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full bg-purple-500 dark:bg-purple-600 rounded-t-sm transition-all hover:bg-purple-400"
                        style={{ height: `${height}%` }}
                        title={`${fmt(m.total_revenue)}`}
                      />
                      <span className={`text-xs ${textMuted}`}>{months[Number(m.month || i+1) - 1]}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className={card}>
          <div className="p-5 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`font-semibold ${textMain}`}>{t('quick_actions')}</h3>
          </div>
          <div className="p-5 space-y-2">
            {quickActions.map((a) => (
              <button
                key={a.label}
                onClick={() => navigate(a.path)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                  isDarkMode
                    ? 'hover:bg-zinc-900 border border-gray-700'
                    : 'hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <a.icon className={a.color} size={18} />
                <span className={`text-sm font-medium ${textMain}`}>{a.label}</span>
                <RiArrowRightLine className={`ml-auto ${textMuted}`} size={14} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Payments Table */}
      <div className={card}>
        <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className={`font-semibold ${textMain}`}>{t('recent_transactions')}</h3>
          <button onClick={() => navigate('/admin/payments')} className="text-sm text-red-500 hover:text-red-400">
            {t('view_all')}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`text-xs font-medium ${textMuted} border-b border-gray-200 dark:border-gray-700`}>
                <th className="text-left p-4">{t('payment_type') || 'Type'}</th>
                <th className="text-left p-4">Unit</th>
                <th className="text-left p-4">{t('payment_date')}</th>
                <th className="text-right p-4">{t('total')}</th>
              </tr>
            </thead>
            <tbody>
              {recentPayments.length === 0 ? (
                <tr>
                  <td colSpan={4} className={`text-center p-6 text-sm ${textMuted}`}>{t('no_recent_payments')}</td>
                </tr>
              ) : recentPayments.map((p) => (
                <tr key={p.paymentId} className={`border-b border-gray-100 dark:border-gray-800 ${isDarkMode ? 'hover:bg-zinc-900' : 'hover:bg-gray-50'} transition-colors`}>
                  <td className="p-4">
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      {p.paymentType}
                    </span>
                  </td>
                  <td className={`p-4 text-sm ${textMain}`}>#{p.apartmentNumber}</td>
                  <td className={`p-4 text-sm ${textMuted}`}>{new Date(p.paymentDate).toLocaleDateString()}</td>
                  <td className={`p-4 text-sm font-semibold ${textMain} text-right`}>{fmt(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccountantDashboard;
